import { openapiClient, unwrapResult } from '@/api/openapi-client';
import { useRestaurantStore } from '@/stores/restaurant.store';
import type {
  CategoryCreateBody,
  CategoryUpdateBody,
  CreatePriceListBody,
  MenuActivation,
  MenuCreateBody,
  MenuUpdateBody,
  OptionChoiceCreateBody,
  OptionChoiceUpdateBody,
  PriceListEntryUpsertBody,
  Product,
  ProductCreateBody,
  ProductOptionCreateBody,
  ProductOptionUpdateBody,
  ProductUpdateBody,
  ProductWithOptionsCreateBody,
} from '@restaurant/api-client';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { trackEvent } from '@/lib/analytics/umami-client';

function useRestaurantId() {
  return useRestaurantStore((s) => s.selectedRestaurantId) ?? '';
}

/** Broad invalidation - the menu tree is small and mutations cross resources. */
function useInvalidateMenu() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: ['menu'] });
}

// ── Queries ────────────────────────────────────────────────────────────────

export function useMenus() {
  const restaurantId = useRestaurantId();
  return useQuery({
    queryKey: ['menu', 'list', restaurantId],
    queryFn: async () => {
      return unwrapResult(
        await openapiClient.GET('/api/restaurants/{restaurantId}/menus', {
          params: { path: { restaurantId } },
        }),
      );
    },
    enabled: !!restaurantId,
  });
}

export function useMenuTree(menuId: string | null | undefined) {
  return useQuery({
    queryKey: ['menu', 'tree', menuId],
    queryFn: async () => {
      return unwrapResult(
        await openapiClient.GET('/api/menus/{menuId}', {
          params: { path: { menuId: menuId ?? '' } },
        }),
      );
    },
    enabled: !!menuId,
  });
}

/**
 * Fetch every supplied menu's tree in parallel. Used where a surface needs the
 * full catalog of a restaurant (e.g. the promotion target picker), not just the
 * one active menu - a restaurant can run several menus (one per channel), so
 * picking from a single tree silently hides items that live in the others.
 */
export function useMenuTrees(menuIds: string[]) {
  return useQueries({
    queries: menuIds.map((menuId) => ({
      queryKey: ['menu', 'tree', menuId],
      queryFn: async () => {
        return unwrapResult(
          await openapiClient.GET('/api/menus/{menuId}', { params: { path: { menuId } } }),
        );
      },
    })),
    combine: (results) => ({
      trees: results.flatMap((r) => (r.data ? [r.data] : [])),
      isLoading: results.some((r) => r.isLoading),
      isError: results.length > 0 && results.every((r) => r.isError),
      hasError: results.some((r) => r.isError),
    }),
  });
}

export function useArchivedProducts(menuId: string | null | undefined) {
  return useQuery({
    queryKey: ['menu', 'archived', menuId],
    queryFn: async () => {
      return unwrapResult(
        await openapiClient.GET('/api/menus/{menuId}/archived-products', {
          params: { path: { menuId: menuId ?? '' } },
        }),
      );
    },
    enabled: !!menuId,
  });
}

export function useRestoreProduct() {
  const invalidate = useInvalidateMenu();
  return useMutation({
    mutationFn: async ({ productId }: { productId: string }) => {
      return unwrapResult(
        await openapiClient.POST('/api/products/{productId}/restore', {
          params: { path: { productId } },
        }),
      );
    },
    onSuccess: invalidate,
  });
}

export function useProduct(productId: string | null | undefined) {
  return useQuery({
    queryKey: ['menu', 'product', productId],
    queryFn: async () => {
      return unwrapResult(
        await openapiClient.GET('/api/products/{productId}', {
          params: { path: { productId: productId ?? '' } },
        }),
      );
    },
    enabled: !!productId && productId !== 'new',
  });
}

export function useListPriceLists() {
  const restaurantId = useRestaurantId();
  return useQuery({
    queryKey: ['menu', 'price-lists', restaurantId],
    queryFn: async () => {
      return unwrapResult(
        await openapiClient.GET('/api/restaurants/{restaurantId}/price-lists', {
          params: { path: { restaurantId } },
        }),
      );
    },
    enabled: !!restaurantId,
  });
}

// ── Price-list mutations ───────────────────────────────────────────────────

export function useCreatePriceList() {
  const restaurantId = useRestaurantId();
  const invalidate = useInvalidateMenu();
  return useMutation({
    mutationFn: async (body: CreatePriceListBody) => {
      return unwrapResult(
        await openapiClient.POST('/api/restaurants/{restaurantId}/price-lists', {
          params: { path: { restaurantId } },
          body,
        }),
      );
    },
    onSuccess: invalidate,
  });
}

export function useUpsertPriceListEntry() {
  const invalidate = useInvalidateMenu();
  return useMutation({
    mutationFn: async ({
      priceListId,
      body,
    }: {
      priceListId: string;
      body: PriceListEntryUpsertBody;
    }) => {
      return unwrapResult(
        await openapiClient.PUT('/api/price-lists/{priceListId}/entries', {
          params: { path: { priceListId } },
          body,
        }),
      );
    },
    onSuccess: invalidate,
  });
}

export function useDeletePriceListEntry() {
  const invalidate = useInvalidateMenu();
  return useMutation({
    mutationFn: async (entryId: string) => {
      return unwrapResult(
        await openapiClient.DELETE('/api/price-list-entries/{entryId}', {
          params: { path: { entryId } },
        }),
      );
    },
    onSuccess: invalidate,
  });
}

// ── Menu mutations ─────────────────────────────────────────────────────────

export function useCreateMenu() {
  const restaurantId = useRestaurantId();
  const invalidate = useInvalidateMenu();
  return useMutation({
    mutationFn: async (body: MenuCreateBody) => {
      return unwrapResult(
        await openapiClient.POST('/api/restaurants/{restaurantId}/menus', {
          params: { path: { restaurantId } },
          body,
        }),
      );
    },
    onSuccess: invalidate,
  });
}

export function useUpdateMenu() {
  const invalidate = useInvalidateMenu();
  return useMutation({
    mutationFn: async ({ menuId, body }: { menuId: string; body: MenuUpdateBody }) => {
      return unwrapResult(
        await openapiClient.PATCH('/api/menus/{menuId}', { params: { path: { menuId } }, body }),
      );
    },
    onSuccess: invalidate,
  });
}

export function useDeleteMenu() {
  const invalidate = useInvalidateMenu();
  return useMutation({
    mutationFn: async ({ menuId }: { menuId: string }) => {
      return unwrapResult(
        await openapiClient.DELETE('/api/menus/{menuId}', { params: { path: { menuId } } }),
      );
    },
    onSuccess: invalidate,
  });
}

export function useMenuActivations() {
  const restaurantId = useRestaurantId();
  return useQuery({
    queryKey: ['menu', 'activations', restaurantId],
    queryFn: async () => {
      return unwrapResult(
        await openapiClient.GET('/api/restaurants/{restaurantId}/menu-activations', {
          params: { path: { restaurantId } },
        }),
      );
    },
    enabled: !!restaurantId,
  });
}

export function useSetMenuActivation() {
  const restaurantId = useRestaurantId();
  const invalidate = useInvalidateMenu();
  return useMutation({
    mutationFn: async ({
      channel,
      menuId,
    }: {
      channel: MenuActivation['channel'];
      menuId: string | null;
    }) => {
      return unwrapResult(
        await openapiClient.PUT('/api/restaurants/{restaurantId}/menu-activations/{channel}', {
          params: { path: { restaurantId, channel } },
          body: { menuId },
        }),
      );
    },
    onSuccess: invalidate,
  });
}

// ── Category mutations ─────────────────────────────────────────────────────

export function useCreateCategory() {
  const invalidate = useInvalidateMenu();
  return useMutation({
    mutationFn: async ({ menuId, body }: { menuId: string; body: CategoryCreateBody }) => {
      return unwrapResult(
        await openapiClient.POST('/api/menus/{menuId}/categories', {
          params: { path: { menuId } },
          body,
        }),
      );
    },
    onSuccess: invalidate,
  });
}

export function useUpdateCategory() {
  const invalidate = useInvalidateMenu();
  return useMutation({
    mutationFn: async ({ categoryId, body }: { categoryId: string; body: CategoryUpdateBody }) => {
      return unwrapResult(
        await openapiClient.PATCH('/api/categories/{categoryId}', {
          params: { path: { categoryId } },
          body,
        }),
      );
    },
    onSuccess: invalidate,
  });
}

export function useDeleteCategory() {
  const invalidate = useInvalidateMenu();
  return useMutation({
    mutationFn: async ({ categoryId }: { categoryId: string }) => {
      return unwrapResult(
        await openapiClient.DELETE('/api/categories/{categoryId}', {
          params: { path: { categoryId } },
        }),
      );
    },
    onSuccess: invalidate,
  });
}

export function useReorderCategories() {
  const invalidate = useInvalidateMenu();
  return useMutation({
    mutationFn: async ({ menuId, orderedIds }: { menuId: string; orderedIds: string[] }) => {
      return unwrapResult(
        await openapiClient.PUT('/api/menus/{menuId}/categories/reorder', {
          params: { path: { menuId } },
          body: { orderedIds },
        }),
      );
    },
    onSuccess: invalidate,
  });
}

// ── Product mutations ──────────────────────────────────────────────────────

export function useCreateProduct() {
  const invalidate = useInvalidateMenu();
  return useMutation({
    mutationFn: async ({ categoryId, body }: { categoryId: string; body: ProductCreateBody }) => {
      return unwrapResult(
        await openapiClient.POST('/api/categories/{categoryId}/products', {
          params: { path: { categoryId } },
          body,
        }),
      );
    },
    onSuccess: invalidate,
  });
}

/**
 * Atomic "advanced add product" create: a product plus its options and their
 * choices in a single request. Mirrors useCreateProduct (same client, same
 * broad ['menu'] invalidation) but hits the /products/full endpoint.
 */
export function useCreateProductWithOptions() {
  const invalidate = useInvalidateMenu();
  return useMutation({
    mutationFn: async ({
      categoryId,
      body,
    }: {
      categoryId: string;
      body: ProductWithOptionsCreateBody;
    }) => {
      return unwrapResult(
        await openapiClient.POST('/api/categories/{categoryId}/products/full', {
          params: { path: { categoryId } },
          body,
        }),
      );
    },
    onSuccess: invalidate,
  });
}

export function useUpdateProduct() {
  const qc = useQueryClient();
  const invalidate = useInvalidateMenu();
  return useMutation({
    mutationFn: async ({ productId, body }: { productId: string; body: ProductUpdateBody }) => {
      return unwrapResult(
        await openapiClient.PATCH('/api/products/{productId}', {
          params: { path: { productId } },
          body,
        }),
      );
    },
    // Optimistic for the availability toggles (isActive/inStock): flip the switch
    // the instant it's released instead of after the round-trip + refetch, which
    // read as a ~5s lag with the switch stuck on its old position. Only the
    // booleans are patched — text/price edits happen in sheets that already show
    // the value locally, so they don't have the snap-back problem.
    onMutate: async ({ productId, body }) => {
      if (body.isActive === undefined && body.inStock === undefined) return;
      const key = ['menu', 'product', productId];
      await qc.cancelQueries({ queryKey: key });
      const previous = qc.getQueryData<Product>(key);
      if (previous) {
        qc.setQueryData<Product>(key, {
          ...previous,
          ...(body.isActive !== undefined ? { isActive: body.isActive } : {}),
          ...(body.inStock !== undefined ? { inStock: body.inStock } : {}),
        });
      }
      return { key, previous };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) qc.setQueryData(ctx.key, ctx.previous);
    },
    onSuccess: (_data, { body }) => {
      if (body.inStock !== undefined) {
        trackEvent('item_stock_toggled', { state: body.inStock ? 'in' : 'out' });
      }
      // Every other product field routes through this same endpoint - name,
      // description, image, schedule, allergens, labels, sortOrder - so listing
      // only price and availability would undercount menu work.
      for (const field of Object.keys(body)) {
        if (field === 'inStock') continue;
        trackEvent('menu_edited', { field: field === 'isActive' ? 'availability' : field });
      }
    },
    // Reconcile with the server on both success and rollback.
    onSettled: invalidate,
  });
}

export function useDeleteProduct() {
  const invalidate = useInvalidateMenu();
  return useMutation({
    mutationFn: async ({ productId }: { productId: string }) => {
      return unwrapResult(
        await openapiClient.DELETE('/api/products/{productId}', {
          params: { path: { productId } },
        }),
      );
    },
    onSuccess: invalidate,
  });
}

// ── Product option mutations ───────────────────────────────────────────────

export function useCreateProductOption() {
  const invalidate = useInvalidateMenu();
  return useMutation({
    mutationFn: async ({
      productId,
      body,
    }: {
      productId: string;
      body: ProductOptionCreateBody;
    }) => {
      return unwrapResult(
        await openapiClient.POST('/api/products/{productId}/options', {
          params: { path: { productId } },
          body,
        }),
      );
    },
    onSuccess: invalidate,
  });
}

export function useUpdateProductOption() {
  const invalidate = useInvalidateMenu();
  return useMutation({
    mutationFn: async ({ optionId, body }: { optionId: string; body: ProductOptionUpdateBody }) => {
      return unwrapResult(
        await openapiClient.PATCH('/api/options/{optionId}', {
          params: { path: { optionId } },
          body,
        }),
      );
    },
    onSuccess: invalidate,
  });
}

export function useDeleteProductOption() {
  const invalidate = useInvalidateMenu();
  return useMutation({
    mutationFn: async ({ optionId }: { optionId: string }) => {
      return unwrapResult(
        await openapiClient.DELETE('/api/options/{optionId}', { params: { path: { optionId } } }),
      );
    },
    onSuccess: invalidate,
  });
}

export function useReorderProductOptions() {
  const qc = useQueryClient();
  const invalidate = useInvalidateMenu();
  return useMutation({
    mutationFn: async ({ productId, orderedIds }: { productId: string; orderedIds: string[] }) => {
      return unwrapResult(
        await openapiClient.PUT('/api/products/{productId}/options/reorder', {
          params: { path: { productId } },
          body: { orderedIds },
        }),
      );
    },
    onMutate: async ({ productId, orderedIds }) => {
      const key = ['menu', 'product', productId];
      await qc.cancelQueries({ queryKey: key });
      const previous = qc.getQueryData<Product>(key);
      if (previous) {
        qc.setQueryData<Product>(key, {
          ...previous,
          options: previous.options.map((option) => {
            const position = orderedIds.indexOf(option.id);
            return position === -1 ? option : { ...option, sortOrder: position };
          }),
        });
      }
      return { key, previous };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) qc.setQueryData(ctx.key, ctx.previous);
    },
    onSettled: invalidate,
  });
}

// ── Option choice mutations ────────────────────────────────────────────────

export function useCreateOptionChoice() {
  const invalidate = useInvalidateMenu();
  return useMutation({
    mutationFn: async ({ optionId, body }: { optionId: string; body: OptionChoiceCreateBody }) => {
      return unwrapResult(
        await openapiClient.POST('/api/options/{optionId}/choices', {
          params: { path: { optionId } },
          body,
        }),
      );
    },
    onSuccess: invalidate,
  });
}

export function useUpdateOptionChoice() {
  const invalidate = useInvalidateMenu();
  return useMutation({
    mutationFn: async ({ choiceId, body }: { choiceId: string; body: OptionChoiceUpdateBody }) => {
      return unwrapResult(
        await openapiClient.PATCH('/api/choices/{choiceId}', {
          params: { path: { choiceId } },
          body,
        }),
      );
    },
    onSuccess: invalidate,
  });
}

export function useDeleteOptionChoice() {
  const invalidate = useInvalidateMenu();
  return useMutation({
    mutationFn: async ({ choiceId }: { choiceId: string }) => {
      return unwrapResult(
        await openapiClient.DELETE('/api/choices/{choiceId}', { params: { path: { choiceId } } }),
      );
    },
    onSuccess: invalidate,
  });
}
